import React, {useEffect, useRef, useState} from 'react';
import {
  Alert,
  AlertTitle,
  Grid2,
  Box,
  IconButton,
  useTheme,
} from '@mui/material';
import {AlertResponse, useDeleteAlertMutation} from '@services/alertApi';
import {keyframes} from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const alertMessages: Record<string, (data: string) => string> = {
  'manage-request': (data) =>
    `${data} requested permission to manage your location.`,
  'manage-request-created': (data) =>
    `A connection request has been sent to ${data}.`,
  'manage-request-created-by-payer': (data) =>
    `A connection has been created to ${data}.`,
  'manage-request-accepted': (data) => `${data} accepted your manage request!`,
  'manage-request-payer-created': (data) =>
    `Great work! You are now a Trusted Hertz Workshop by ${data}.`,
  'manage-request-payer-removed': (data) => `${data} disconnected from you.`,
  'manage-request-declined': (data) => `${data} denied your manage request.`,
  'manage-request-disconnected': (data) => `${data} disconnected from you.`,
  'manage-request-revoked': (data) => `${data} revoked your manage permission.`,
  restart: (data) => `Ebook will go down for maintenance ${data}.`,
  'generic-message': (data) => data,
};

const blinkAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
`;

const getSeverityIcon = (
  severity: Severity,
) => {
  switch (severity) {
    case 'info':
      return <InfoIcon/>;
    case 'warning':
      return <WarningIcon/>;
    case 'error':
      return <ErrorIcon/>;
    case 'success':
      return <CheckCircleIcon/>;
    default:
      return null;
  }
};

type Severity = 'info' | 'warning' | 'error' | 'success';

interface AlertBannerProps {
  alertData: AlertResponse[];
  onHeightChange: (height: number) => void;
  onClose: () => void;
}

const AlertBanner: React.FC<AlertBannerProps> = ({
  alertData,
  onHeightChange,
  onClose,
}) => {
  const theme = useTheme();
  const alertBannerRef = useRef<HTMLDivElement>(null);
  const [deleteAlert] = useDeleteAlertMutation();
  const [closedAlertIds, setClosedAlertIds] = useState<number[]>([]);

  useEffect(() => {
    alertData.length <= 0 && onClose();
  }, [alertData, onClose]);

  useEffect(() => {
    if (alertBannerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (alertBannerRef.current) {
          const newHeight = alertBannerRef.current.offsetHeight;
          onHeightChange(newHeight);
        }
      });

      resizeObserver.observe(alertBannerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [onHeightChange]);

  const blinkingIconStyle = (severity: Severity) => (severity === 'error' ? {
    animation: `${blinkAnimation} 0.5s infinite`,
  } : null);

  const root = {
    position: 'fixed',
    top: '3.5rem',
    width: '100%',
    zIndex: 1000,
  };

  const alertContainer = {
    position: 'relative',
    textAlign: 'center',
    width: '100%',
    paddingRight: '3rem',
    display: 'flex',
    justifyContent: 'center',
  };

  const closeBtn = {
    color: theme.palette.secondary.dark,
    position: 'absolute',
    top: '.3rem',
    right: 0,
    zIndex: 1100,
  };

  const getAlertSeverity = (level?: number): Severity => {
    return level === 1 ? 'warning' : level && level >= 2 ? 'error' : 'info';
  };

  const tryDeleteAlert = (alertId: number) => {
    deleteAlert({
      id: alertId,
    })
      .then(() => {
        console.log('successfully deleted alert');
        setClosedAlertIds([...closedAlertIds, alertId]);
      })
      .catch((error) => {
        console.error(`Error in delete operation: \n`, error);
      });
  };

  const handleAlertDelete = (index: number) => {
    const {id} = alertData.find((alert) => alert === alertData[index]);

    if (!id) return;
    tryDeleteAlert(id);
  };

  return (
    <Grid2 container sx={root} ref={alertBannerRef}>
      {alertData.map((alert, index) => {

        if (closedAlertIds.includes(alert.id)) return null;
        const severity: Severity = getAlertSeverity(alert.level)
        return (
          <Alert
            key={index}
            sx={alertContainer}
            severity={severity}
            icon={
              <Box sx={blinkingIconStyle(severity)}>
                {getSeverityIcon(severity)}
              </Box>
            }
          >
            {!alert.unclosable && (
              <IconButton onClick={() => handleAlertDelete(index)} sx={closeBtn}>
                <CloseIcon sx={{fontSize: '1.6rem'}}/>
              </IconButton>
            )}
            <AlertTitle sx={{mb: 0}}>
              {alertMessages[alert.name]?.(alert.data) || ''}
            </AlertTitle>
          </Alert>
        )
      })}
    </Grid2>
  );
};

export default AlertBanner;
